import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, ArrowUp, ArrowDown, Bitcoin } from "lucide-react";

const COINS = ["BTC", "ETH", "SOL"];
const COIN_ICON = { BTC: "₿", ETH: "Ξ", SOL: "◎" };

function CountdownBoxes({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(to) - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const boxes = [
    { v: d, l: "Days" },
    { v: h, l: "Hrs" },
    { v: m, l: "Mins" },
    { v: s, l: "Secs" },
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {boxes.map((b) => (
        <div key={b.l} className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg py-2 text-center">
          <div className="font-display font-bold text-lg text-[var(--text-primary)]">{String(b.v).padStart(2, "0")}</div>
          <div className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{b.l}</div>
        </div>
      ))}
    </div>
  );
}

export default function WeeklyOverUnder() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [results, setResults] = useState(null);
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("play");

  const load = async () => {
    try {
      const [{ data: cur }, { data: res }] = await Promise.all([
        api.get("/community/weekly/current"),
        api.get("/community/weekly/results"),
      ]);
      setData(cur);
      setResults(res);
      if (cur.my_picks) setPicks(cur.my_picks.picks);
    } catch {}
  };

  useEffect(() => { load(); }, [user?.id]);

  if (!data) return null;
  const { round, locked, my_picks, split } = data;

  const submit = async (coin, dir) => {
    if (locked || !user) return;
    if (user) {
      const newPicks = { ...picks, [coin]: dir };
      setPicks(newPicks);
      if (Object.keys(newPicks).length < 3) return; // wait until all 3 chosen, or submit per-pick individually below
    }
  };

  const submitAll = async () => {
    if (!COINS.every((c) => picks[c])) return toast.error("Pick above or below for all 3 coins");
    setSaving(true);
    try {
      const { data: r } = await api.post("/community/weekly/picks", { picks });
      toast.success(r.updated ? "Picks updated" : "Picks locked in");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-5 relative overflow-hidden">
      <div className="absolute -right-6 -top-6 opacity-[0.06]"><Bitcoin className="w-32 h-32 text-yellow-400" /></div>

      <div className="relative flex items-center gap-2 mb-1">
        <CalendarDays className="w-4 h-4 text-yellow-400" />
        <h3 className="font-display font-bold text-sm uppercase tracking-wide text-[var(--text-primary)]">Weekly Call</h3>
      </div>
      <div className="relative text-xs font-mono text-[var(--text-tertiary)] mb-4">
        {round.week_key} · {locked ? "Locked" : "Resolves"} {new Date(round.close_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
      </div>

      <p className="relative text-sm text-[var(--text-secondary)] mb-4">
        Will each coin close the week above or below Monday's price?
      </p>

      {!user ? (
        <Link to="/register" className="relative block text-center bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-4 py-2.5 hover:bg-yellow-500 transition text-sm">
          Join free to play
        </Link>
      ) : (
        <div className="relative space-y-3 mb-4">
          {COINS.map((c) => {
            const pct = split?.above_pct?.[c];
            const picked = my_picks?.picks?.[c] || picks[c];
            return (
              <div key={c}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-[var(--text-primary)] flex items-center gap-1.5">
                    <span className="text-yellow-400">{COIN_ICON[c]}</span> {c}
                  </span>
                  <span className="text-xs font-mono text-[var(--text-tertiary)]">${Number(round.lines[c]).toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={locked}
                    onClick={() => setPicks({ ...picks, [c]: "above" })}
                    className={"flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold uppercase tracking-wide transition disabled:cursor-default " +
                      (picked === "above" ? "bg-green-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] hover:text-green-400 border border-[var(--border-soft)]")}>
                    <ArrowUp className="w-3.5 h-3.5" /> Above {pct != null && locked ? `${pct}%` : ""}
                  </button>
                  <button
                    disabled={locked}
                    onClick={() => setPicks({ ...picks, [c]: "below" })}
                    className={"flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold uppercase tracking-wide transition disabled:cursor-default " +
                      (picked === "below" ? "bg-[var(--text-primary)] text-[var(--bg-base)]" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] hover:text-red-400 border border-[var(--border-soft)]")}>
                    <ArrowDown className="w-3.5 h-3.5" /> Below {pct != null && locked ? `${100 - pct}%` : ""}
                  </button>
                </div>
              </div>
            );
          })}
          {!locked && (
            <button onClick={submitAll} disabled={saving}
              className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-4 py-2.5 hover:bg-yellow-500 transition disabled:opacity-50 text-sm">
              {saving ? "Saving..." : my_picks ? "Update Picks" : "Lock In Picks"}
            </button>
          )}
        </div>
      )}

      <p className="relative text-[10px] text-[var(--text-tertiary)] mb-4 leading-snug">
        Payouts depend on correct prediction. Play for reputation, tracked publicly. Serious traders only.
      </p>

      {!locked && <CountdownBoxes to={round.lock_at} />}

      {results?.round && (
        <button onClick={() => setTab(tab === "results" ? "play" : "results")}
          className="relative w-full mt-4 text-xs font-mono text-yellow-400 hover:underline text-center">
          {tab === "results" ? "← Back" : "View Last Week's Results →"}
        </button>
      )}

      {tab === "results" && results?.round && (
        <div className="relative mt-4 pt-4 border-t border-[var(--border-soft)] space-y-2">
          {COINS.map((c) => {
            const up = results.round.outcomes?.[c] === "above";
            return (
              <div key={c} className="flex items-center justify-between text-xs font-mono">
                <span className="text-[var(--text-tertiary)]">{c}</span>
                <span className={up ? "text-green-400" : "text-red-400"}>
                  {up ? "▲" : "▼"} ${Number(results.round.closing_prices?.[c] || 0).toLocaleString()}
                </span>
              </div>
            );
          })}
          {results.results.slice(0, 3).map((r, i) => (
            <div key={r.user_id} className="flex items-center justify-between text-xs">
              <Link to={`/profile/${r.user_id}`} className="text-[var(--text-secondary)] hover:text-yellow-400 truncate max-w-[100px]">{i + 1}. {r.user_name}</Link>
              <span className="font-mono text-yellow-400">{r.correct}/3</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}